import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CategoriesProvider } from "@/contexts/CategoriesContext";
import Layout from "@/components/Layout";
import { OfflineBanner } from "@/components/OfflineBanner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import Budgets from "@/pages/Budgets";
import Accounts from "@/pages/Accounts";
import Bills from "@/pages/Bills";
import Reports from "@/pages/Reports";
import Statements from "@/pages/Statements";
import Journal from "@/pages/Journal";
import AuthCallback from "@/pages/AuthCallback";
import Ask from "@/pages/Ask";
import Recurring from "@/pages/Recurring";
import Landing from "@/pages/Landing";
import StoreHome from "@/pages/StoreHome";
import Pricing from "@/pages/Pricing";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import SharedStatements from "@/pages/SharedStatements";
import Categories from "@/pages/Categories";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6C7565] font-mono text-xs uppercase tracking-widest">Loading ledger…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

// "/" → FinLedger marketing for visitors, straight to the app for signed-in users.
function FinLedgerGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6C7565] font-mono text-xs uppercase tracking-widest">Loading ledger…</div>;
  if (user) return <Navigate to="/app" replace />;
  return <Landing />;
}

// Synchronously intercept #session_id in URL to run OAuth callback first
function AppRouter() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<FinLedgerGate />} />
      <Route path="/store" element={<StoreHome />} />
      <Route path="/finledger" element={<Navigate to="/" replace />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/shared/:token" element={<SharedStatements />} />
      <Route element={<Protected><CategoriesProvider><Layout /></CategoriesProvider></Protected>}>
        <Route path="/app" element={<Dashboard />} />
        <Route path="/transactions" element={<Transactions />} />
        <Route path="/budgets" element={<Budgets />} />
        <Route path="/accounts" element={<Accounts />} />
        <Route path="/bills" element={<Bills />} />
        <Route path="/recurring" element={<Recurring />} />
        <Route path="/categories" element={<Categories />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/statements" element={<Statements />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/ask" element={<Ask />} />
        <Route path="/pricing" element={<Pricing />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
      <OfflineBanner />
    </AuthProvider>
  );
}

export default App;
